import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Lock, User, ShieldAlert } from 'lucide-react';
import { motion } from 'framer-motion';

import axios from 'axios';
import { useNombreApp } from '../../hooks/useNombreApp';

export default function PublicLogin() {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [codigo, setCodigo] = useState('');
    // El servidor valida la contrasena primero y recien entonces pide el
    // codigo. Hasta que no lo pida, el campo no se muestra: anticiparlo
    // revelaria que esa cuenta existe y que tiene segundo factor activo.
    const [pideCodigo, setPideCodigo] = useState(false);
    const [recordarEquipo, setRecordarEquipo] = useState(true);
    const nombreApp = useNombreApp();
    const [error, setError] = useState('');
    const navigate = useNavigate();

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');

        try {
            const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
            // El valor que recuerda este equipo vive en localStorage: sobrevive
            // al cierre del navegador, que es justamente lo que se busca. No
            // reemplaza a la contraseña, solo exime del código.
            const equipo = localStorage.getItem('equipo_confiable');

            const response = await axios.post(`${BASE}/api/v1/auth/token/`, {
                username: username,
                password: password,
                ...(codigo ? { codigo: codigo.trim() } : {}),
                ...(equipo ? { dispositivo: equipo } : {}),
                ...(codigo && recordarEquipo ? { recordar_equipo: true } : {}),
            });

            if (response.data.dispositivo) {
                localStorage.setItem('equipo_confiable', response.data.dispositivo);
            }

            // Tokens de superadmin en claves exclusivas para no pisar sesiones de tenant
            localStorage.setItem('admin_access_token', response.data.access);
            localStorage.setItem('admin_refresh_token', response.data.refresh);
            localStorage.setItem('user_type', 'admin');

            navigate('/admin');
        } catch (err) {
            if (err.response?.data?.requiere_segundo_factor) {
                // El servidor pide el código: lo que hubiera guardado para este
                // equipo ya no vale (venció o fue revocado). Se descarta para no
                // seguir enviando un valor muerto en cada intento.
                localStorage.removeItem('equipo_confiable');
                setPideCodigo(true);
                setCodigo('');
                setError(err.response.data.error || '');
            } else if (err.response?.status === 429) {
                const seg = err.response.headers?.['retry-after'];
                setError(
                    seg
                        ? `Demasiados intentos. Esperá ${Math.ceil(seg / 60)} minuto(s) y volvé a probar.`
                        : 'Demasiados intentos. Esperá unos minutos y volvé a probar.'
                );
            } else {
                setPideCodigo(false);
                setCodigo('');
                setError('Credenciales incorrectas. Intente nuevamente.');
            }
        }
    }

    return (
        <div className="pantalla-centrada">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-md"
            >
                <div className="bg-white rounded-3xl shadow-xl p-8 relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-8 -mt-8 -mr-8 w-32 h-32 bg-brand-500 rounded-full blur-3xl opacity-20" />
                    <div className="absolute bottom-0 left-0 p-8 -mb-8 -ml-8 w-32 h-32 bg-brand-500 rounded-full blur-3xl opacity-20" />

                    <div className="mb-8 text-center relative z-10">
                        <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">{nombreApp}</h1>
                        <p className="text-slate-500 mt-2 font-medium">Panel de Administración</p>
                    </div>

                    <form onSubmit={handleLogin} className="space-y-6 relative z-10">
                        {error && (
                            <div className="bg-error-50 text-error-500 p-3 rounded-lg text-sm text-center font-medium border border-error-100">
                                {error}
                            </div>
                        )}
                        <div>
                            <label htmlFor="admin-usuario" className="etiqueta-simple">Usuario o correo</label>
                            <div className="relative">
                                <div className="icono-campo-interno">
                                    <User size={18} />
                                </div>
                                <input id="admin-usuario"
                                    type="text"
                                    className="block w-full pl-10 pr-3 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-slate-50 font-medium transition-colors"
                                    placeholder="admin o tucorreo@ejemplo.com"
                                    value={username}
                                    onChange={e => setUsername(e.target.value)}
                                />
                            </div>
                        </div>

                        <div>
                            <label htmlFor="admin-clave" className="etiqueta-simple">Contraseña</label>
                            <div className="relative">
                                <div className="icono-campo-interno">
                                    <Lock size={18} />
                                </div>
                                <input id="admin-clave"
                                    type="password"
                                    className="block w-full pl-10 pr-3 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-slate-50 font-medium transition-colors"
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                />
                            </div>
                        </div>

                        {pideCodigo && (
                            <div>
                                <label htmlFor="admin-codigo" className="etiqueta-simple">
                                    Codigo de verificacion
                                </label>
                                <input
                                    id="admin-codigo"
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    autoFocus
                                    maxLength={14}
                                    className="campo-codigo"
                                    placeholder="000000"
                                    value={codigo}
                                    onChange={e => setCodigo(e.target.value)}
                                />
                                <p className="text-xs text-slate-400 mt-2 text-center">
                                    El de seis dígitos que muestra tu aplicación de autenticación.
                                    Cambia solo cada 30 segundos.
                                </p>
                                <label htmlFor="admin-recordar" className="flex items-start gap-2 mt-4 cursor-pointer select-none">
                                    <input
                                        id="admin-recordar"
                                        type="checkbox"
                                        checked={recordarEquipo}
                                        onChange={e => setRecordarEquipo(e.target.checked)}
                                        className="mt-0.5 w-4 h-4 rounded border-slate-300 text-brand-800 focus:ring-brand-500/50"
                                    />
                                    <span className="text-xs text-slate-600 leading-relaxed">
                                        No volver a pedirme el código en este equipo por 30 días.
                                        <span className="block text-slate-400">
                                            Marcalo solo si la computadora es tuya.
                                        </span>
                                    </span>
                                </label>
                            </div>
                        )}

                        <button
                            type="submit"
                            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-lg shadow-brand-500/30 font-semibold text-negro-950 bg-brand-600 hover:bg-brand-500 hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500"
                        >
                            Iniciar Sesión
                        </button>
                    </form>

                    <div className="mt-6 pt-5 border-t border-slate-100 text-center space-y-3">
                        <Link
                            to="/olvide-contrasena?origen=admin"
                            className="block text-sm text-brand-800 hover:text-brand-800 font-semibold transition-colors"
                        >
                            ¿Olvidaste tu contraseña?
                        </Link>
                        <Link
                            to="/admin/recuperar"
                            className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-alerta-600 font-medium transition-colors"
                        >
                            <ShieldAlert size={13} />
                            ¿Sin acceso al correo? Usar código de emergencia
                        </Link>
                    </div>
                </div>
            </motion.div>
        </div>
    );
}

